/**
 * Canonical allow-list for tenant Video Asset upload
 * (VistoeraTASK-00220 — extracted from `VideoAssetPicker.tsx`'s
 * previously-private `ACCEPTED_MIME`/`ACCEPTED_EXT`/`isAcceptedFile` so
 * the Canvas OS File Drop pipeline and the picker's own upload tile
 * share exactly ONE definition, never two copies that could drift —
 * same rationale/shape as `audioUploadAllowlist.ts`).
 *
 * Keep in sync with the backend's real accepted set:
 * `app.core.config.Settings.video_gen_video_allowed_mime` /
 * `VideoAssetPicker.tsx`'s original `ACCEPTED_MIME`/`ACCEPTED_EXT`
 * (unchanged values — this file only centralizes them).
 */

export const VIDEO_UPLOAD_EXTENSIONS = ['.mp4', '.mov', '.webm'] as const

export const VIDEO_UPLOAD_MIME_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'] as const

const MIME_SET = new Set<string>(VIDEO_UPLOAD_MIME_TYPES)
const EXT_SET = new Set<string>(VIDEO_UPLOAD_EXTENSIONS)

export function buildVideoUploadFileInputAccept(): string {
  return [...VIDEO_UPLOAD_MIME_TYPES, ...VIDEO_UPLOAD_EXTENSIONS].join(',')
}

/** MIME first, filename extension as a fallback — mirrors
 * `isAllowedAudioAssetUploadFile`'s and `canvasFileDrop.ts`'s
 * `isAcceptedImageFile`'s posture (never extension-only). Identical
 * logic to the picker's original private `isAcceptedFile`. */
export function isAllowedVideoAssetUploadFile(file: Pick<File, 'type' | 'name'>): boolean {
  const type = (file.type || '').toLowerCase()
  if (MIME_SET.has(type)) return true
  const lower = (file.name || '').toLowerCase()
  return [...EXT_SET].some((ext) => lower.endsWith(ext))
}
